let gsapPromise = null;
const pluginPromises = Object.create(null); // { [name]: Promise<{gsap, plugin}> }
const _pendingLoads = new Map();            // src -> Promise<void>

/* --------------------------------------------
 * Helpers
 * ------------------------------------------*/
function getAssets() {
  return (typeof window !== 'undefined' && window.__GSAP_ASSETS) ? window.__GSAP_ASSETS : {};
}

function currentNonce() {
  // Try to preserve CSP nonces if present
  try {
    const s = document.currentScript;
    if (s && s.nonce) return s.nonce;
    const meta = document.querySelector('meta[property="csp-nonce"], meta[name="csp-nonce"]');
    if (meta?.content) return meta.content;
  } catch {}
  return null;
}

function loadScript(src) {
  if (!src) return Promise.reject(new Error('[GSAPLoader] Missing script src'));
  if (_pendingLoads.has(src)) return _pendingLoads.get(src);

  // If an identical script tag already completed, resolve immediately
  const found = document.querySelector(`script[data-gsap-src="${CSS.escape(src)}"]`);
  if (found?.dataset.loaded === 'true') return Promise.resolve();

  const p = new Promise((resolve, reject) => {
    // If an identical tag exists and is still loading, hook its events
    if (found) {
      found.addEventListener('load', resolve, { once: true });
      found.addEventListener('error', () => reject(new Error(`[GSAPLoader] Failed to load: ${src}`)), { once: true });
      return;
    }

    const s = document.createElement('script');
    s.async = true;
    s.defer = true;
    s.dataset.gsapSrc = src;
    const nonce = currentNonce();
    if (nonce) s.nonce = nonce;

    s.addEventListener('load', () => {
      s.dataset.loaded = 'true';
      resolve();
    }, { once: true });

    s.addEventListener('error', () => {
      reject(new Error(`[GSAPLoader] Failed to load: ${src}`));
    }, { once: true });

    s.src = src;
    document.head.appendChild(s);
  });

  _pendingLoads.set(src, p);
  return p.finally(() => {
    // keep the promise cached so future callers resolve immediately
    // (but don't delete to avoid race/new tag creation)
  });
}

/* --------------------------------------------
 * Public API (ESM exports)
 * ------------------------------------------*/
export function loadGSAP() {
  if (typeof window === 'undefined') return Promise.reject(new Error('[GSAPLoader] No window'));
  if (window.gsap) return Promise.resolve(window.gsap);
  if (gsapPromise) return gsapPromise;

  const A = getAssets();
  const coreUrl = A.core;
  if (!coreUrl) {
    return Promise.reject(new Error('[GSAPLoader] GSAP asset URL not configured (window.__GSAP_ASSETS.core)'));
  }

  gsapPromise = loadScript(coreUrl).then(() => {
    const gsap = window.gsap;
    if (!gsap) throw new Error('[GSAPLoader] GSAP loaded but window.gsap not found');
    return gsap;
  });

  return gsapPromise;
}

export function loadGSAPPlugin(name) {
  if (!name) return Promise.reject(new Error('[GSAPLoader] Plugin name is required'));
  if (pluginPromises[name]) return pluginPromises[name];

  const A = getAssets();
  const url = A[name];
  if (!url) {
    return Promise.reject(new Error(`[GSAPLoader] No asset URL for plugin "${name}" in window.__GSAP_ASSETS`));
  }

  pluginPromises[name] = loadGSAP()
    .then((gsap) => loadScript(url).then(() => {
      const plugin = window[name];
      if (!plugin) throw new Error(`[GSAPLoader] ${name} loaded but window.${name} not found`);
      // Auto-register if possible (idempotent in GSAP)
      try { gsap.registerPlugin(plugin); } catch {}
      return { gsap, plugin };
    }));

  return pluginPromises[name];
}

export async function loadGSAPPlugins(names = []) {
  if (!Array.isArray(names)) names = [names];
  const results = await Promise.all(names.map(loadGSAPPlugin));
  const gsap = results[0]?.gsap || (await loadGSAP());
  const plugins = Object.fromEntries(results.map((r, i) => [names[i], r.plugin]));
  return { gsap, plugins };
}

/* --------------------------------------------
 * Global handshake for non-module scripts
 * ------------------------------------------*/
const __gsapReadyPromise = (async () => {
  try { await loadGSAP(); } catch (err) { console.error(err); }
  return true;
})();

(function exposeGlobal() {
  // Preserve an existing global if one exists; extend it
  const existing = window.__gsapLoader || {};
  window.__gsapLoader = {
    version: '1.2.0',
    ready: existing.ready || (() => __gsapReadyPromise),
    load: existing.load || loadGSAP,
    plugin: existing.plugin || ((name) => loadGSAPPlugin(name)),
    plugins: existing.plugins || ((names) => loadGSAPPlugins(names))
  };
})();